import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react';
import ShopifyBuyButton from '../components/ShopifyBuyButton';

// Shopify configuration for fetching product images
const SHOPIFY_CONFIG = {
  domain: 'x16ha3-n1.myshopify.com',
  storefrontAccessToken: 'f1f5374c07806735e6632074755592e5',
};

// Accordion component
const Accordion = ({ title, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border-b border-plumo-black/10">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-4"
        style={{
          fontFamily: "'Space Mono', monospace",
          fontSize: '12px',
          letterSpacing: '0.05em',
          color: '#1A1814',
        }}
      >
        <span>{title}</span>
        {isOpen ? (
          <ChevronUp className="w-4 h-4 text-plumo-black/40" />
        ) : (
          <ChevronDown className="w-4 h-4 text-plumo-black/40" />
        )}
      </button>
      {isOpen && (
        <div 
          className="pb-4"
          style={{
            fontFamily: "'Space Mono', monospace",
            fontSize: '12px',
            color: 'rgba(26,24,20,0.6)',
            lineHeight: 1.7,
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
};

const TheBag = () => {
  const [selectedImage, setSelectedImage] = useState(0);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch product images from Shopify
  useEffect(() => {
    const fetchProductImages = async () => {
      const query = `
        query getProduct($id: ID!) {
          node(id: $id) {
            ... on Product {
              images(first: 10) {
                edges {
                  node {
                    url
                  }
                }
              }
            }
          }
        }
      `;

      try {
        const response = await fetch(
          `https://${SHOPIFY_CONFIG.domain}/api/2024-01/graphql.json`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': SHOPIFY_CONFIG.storefrontAccessToken,
            },
            body: JSON.stringify({
              query,
              variables: { id: 'gid://shopify/Product/9175528407294' },
            }),
          }
        );

        const data = await response.json();
        const node = data.data?.node;
        
        if (node) {
          const productImages = node.images?.edges?.map(edge => edge.node.url) || [];
          setImages(productImages);
        }
      } catch (err) {
        console.error('Error fetching product images:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProductImages();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-plumo-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-12">
          <div className="animate-pulse">
            <div className="h-4 bg-plumo-bone w-24 mb-12" />
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
              <div className="lg:col-span-3 aspect-[4/5] bg-plumo-bone" />
              <div className="lg:col-span-2 space-y-6 lg:pt-20">
                <div className="h-8 bg-plumo-bone w-3/4" />
                <div className="h-5 bg-plumo-bone w-1/4" />
                <div className="h-20 bg-plumo-bone" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-plumo-white" data-testid="product-page-bag">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-8">
        {/* Back Link */}
        <Link
          to="/shop"
          className="inline-flex items-center gap-2 mb-8"
          style={{
            fontFamily: "'Space Mono', monospace",
            fontSize: '11px',
            letterSpacing: '0.1em',
            color: 'rgba(26,24,20,0.5)',
          }}
          data-testid="back-to-shop"
        >
          <ArrowLeft className="w-3 h-3" />
          back
        </Link>

        {/* Main Content - Nomès style layout */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-16">
          {/* LEFT COLUMN (60% = 3/5) - Large product image */}
          <div className="lg:col-span-3">
            <div className="aspect-[4/5] bg-plumo-bone overflow-hidden">
              {images.length > 0 && (
                <img
                  src={images[selectedImage]}
                  alt="The Bag"
                  className="w-full h-full object-cover"
                  data-testid="product-main-image"
                />
              )}
            </div>
            
            {/* Thumbnail selector on desktop */}
            {images.length > 1 && (
              <div className="hidden lg:flex gap-3 mt-4">
                {images.slice(0, 5).map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`w-16 h-20 overflow-hidden transition-opacity ${
                      selectedImage === index ? 'opacity-100' : 'opacity-40 hover:opacity-70'
                    }`}
                    data-testid={`thumbnail-${index}`}
                  >
                    <img
                      src={img}
                      alt={`The Bag view ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* RIGHT COLUMN (40% = 2/5) - Product info */}
          <div className="lg:col-span-2 lg:pt-20">
            {/* Product Name */}
            <h1 
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontStyle: 'italic',
                fontSize: '32px',
                color: '#1A1814',
                lineHeight: 1.1,
                marginBottom: '16px',
              }}
              data-testid="product-name"
            >
              The Bag
            </h1>

            {/* Price - Inline strikethrough + sale */}
            <div 
              className="mb-2"
              data-testid="product-price"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontStyle: 'italic',
                fontSize: '18px',
              }}
            >
              <span 
                style={{
                  color: 'rgba(26,24,20,0.4)',
                  textDecoration: 'line-through',
                  marginRight: '12px',
                }}
              >
                $40
              </span>
              <span style={{ color: '#1A1814' }}>
                $18
              </span>
            </div>

            {/* Tax line */}
            <p 
              style={{
                fontFamily: "'Space Mono', monospace",
                fontSize: '11px',
                color: 'rgba(26,24,20,0.5)',
                marginBottom: '24px',
              }}
            >
              tax included.
            </p>

            {/* Material / Size / Details */}
            <div 
              className="mb-6 space-y-2"
              style={{
                fontFamily: "'Space Mono', monospace",
                fontSize: '13px',
                lineHeight: 1.7,
              }}
            >
              <p>
                <span style={{ color: '#1A1814' }}>material: </span>
                <span style={{ color: 'rgba(26,24,20,0.7)' }}>crinkled leather</span>
              </p>
              <p>
                <span style={{ color: '#1A1814' }}>hardware: </span>
                <span style={{ color: 'rgba(26,24,20,0.7)' }}>gold-plated brass</span>
              </p>
              <p>
                <span style={{ color: '#1A1814' }}>size: </span>
                <span style={{ color: 'rgba(26,24,20,0.7)' }}>30cm × 15cm × 8cm</span>
              </p>
              <p>
                <span style={{ color: '#1A1814' }}>strap: </span>
                <span style={{ color: 'rgba(26,24,20,0.7)' }}>130cm, detachable</span>
              </p>
            </div>

            {/* Divider */}
            <div className="border-t border-plumo-black/10 my-6" />

            {/* Shopify Buy Button */}
            <ShopifyBuyButton productId="bag" />

            {/* Below button text */}
            <p 
              className="mt-6"
              style={{
                fontFamily: "'Space Mono', monospace",
                fontSize: '12px',
                color: 'rgba(26,24,20,0.5)',
                lineHeight: 1.7,
              }}
            >
              each piece carefully made. free 30-day returns. shipped from california.
            </p>

            {/* Accordion sections */}
            <div className="mt-8">
              <Accordion title="reviews" defaultOpen={false}>
                <p>★★★★★ 4.9 average rating</p>
                <p className="mt-2">"arrived fast, looks way more expensive than it is. fits everything — phone, wallet, the works." — J.</p>
              </Accordion>
              
              <Accordion title="shipping" defaultOpen={false}>
                <p>all orders ship within 2-3 business days.</p>
                <p className="mt-2">free shipping on orders over $50.</p>
                <p>standard delivery 5-7 business days.</p>
              </Accordion>
              
              <Accordion title="care" defaultOpen={false}>
                <p>store in a cool, dry place away from direct sunlight.</p>
                <p className="mt-2">wipe gently with a damp cloth.</p>
                <p>avoid prolonged contact with water.</p>
              </Accordion>
              
              <Accordion title="sizing" defaultOpen={false}>
                <p>one size.</p>
                <p className="mt-2">dimensions: 30cm × 15cm × 8cm</p>
                <p>strap length: 130cm (detachable)</p>
              </Accordion>
            </div>
          </div>
        </div>

        {/* Additional Images - Full width below */}
        {images.length > 1 && (
          <div className="mt-24 space-y-4">
            {images.slice(1, 5).map((img, index) => (
              <div key={index} className="w-full">
                <img
                  src={img}
                  alt={`The Bag detail ${index + 2}`}
                  className="w-full h-auto"
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TheBag;
